import { ApplicationConfig, importProvidersFrom, LOCALE_ID, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, withRouterConfig, withEnabledBlockingInitialNavigation } from '@angular/router';

import { routes } from './app.routes';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import localeDe from '@angular/common/locales/de';
import { BASE_PATH } from './shared/generated/backend';
import deMessages from 'devextreme/localization/messages/de.json';
import niclenMessages from './../assets/i18n/de.json';
import { registerLocaleData } from '@angular/common';
import { locale, loadMessages } from 'devextreme/localization';
import { CustomTranslateHttpLoader } from './CustomTranslateHttpLoader';
import { firstValueFrom } from 'rxjs';

registerLocaleData(localeDe);
loadMessages(deMessages);
loadMessages(niclenMessages);
locale('de');

export function loggerCallback(_: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureActiveDirectory.clientId,
      authority: environment.azureActiveDirectory.authorities.signUpSignIn,
      knownAuthorities: [environment.azureActiveDirectory.authorityDomain],
      redirectUri: `${window.location.origin}/redirect`,
      postLogoutRedirectUri: `${window.location.origin}/redirect`,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      temporaryCacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    `${environment.backend.baseUrl}${environment.backend.apiUrl}`,
    environment.azureActiveDirectory.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.azureActiveDirectory.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const provideTranslation = () => ({
  defaultLanguage: 'de',
  loader: {
    provide: TranslateLoader,
    useClass: CustomTranslateHttpLoader,
    deps: [HttpClient],
  },
});

export function initTranslations(translate: TranslateService) {
  return async () => {
    const lang = 'de';
    translate.setDefaultLang('de');
    translate.use(lang);

    await firstValueFrom(translate.get('ASSIGNMENT.STATE.OPEN'));
  };
}

export function initMsal(msalService: MsalService) {
  return async () => {
    await msalService.instance.initialize();

    const result = await msalService.instance.handleRedirectPromise();

    if (result?.account) {
      msalService.instance.setActiveAccount(result.account);
      return;
    }

    const activeAccount = msalService.instance.getActiveAccount();
    if (activeAccount) {
      return;
    }

    const accounts = msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      msalService.instance.setActiveAccount(accounts[0]);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      })
    ),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: BASE_PATH, useValue: environment.backend.baseUrl },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    importProvidersFrom([TranslateModule.forRoot(provideTranslation())]),
    provideAppInitializer(() => {
      const initializerFn = initTranslations(inject(TranslateService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initMsal(inject(MsalService));
      return initializerFn();
    }),
    { provide: LOCALE_ID, useValue: 'de-DE' },
    provideAnimationsAsync(),
  ],
};
